import { attendanceCellStatus, attendanceCellSymbol, formatWork, myAttendanceCellText, type AttendanceCellStatus } from '@mt/shared';
import type { TimesheetCell } from './attendance-api';

/** Màu ô bảng công theo trạng thái (token semantic, chạy cả chế độ Tối). Chữ/ký hiệu đi kèm nên màu không là tín hiệu duy nhất. */
export const CELL_CLASS: Record<AttendanceCellStatus, string> = {
  full: 'bg-success-soft text-success-foreground',
  half: 'bg-info-soft text-info-foreground',
  late: 'bg-warning-soft text-warning-foreground',
  problem: 'bg-warning-soft text-warning-foreground ring-1 ring-inset ring-warning font-semibold',
  absent: 'bg-error-soft text-error-foreground font-semibold',
  working: 'bg-info-soft text-info-foreground',
  pending: 'text-muted-foreground',
  off: 'bg-muted text-muted-foreground',
  sunday_work: 'bg-info-soft text-info-foreground font-semibold ring-1 ring-inset ring-info',
  future: 'text-muted-foreground',
  leave: 'bg-success-soft text-success-foreground font-semibold',
  leave_unpaid: 'bg-neutral-soft text-muted-foreground font-semibold',
};

/** Hôm nay theo giờ VN (YYYY-MM-DD). */
export const todayVN = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);
export const thisMonth = () => todayVN().slice(0, 7);
/** Cộng ngày cho ngày ISO (YYYY-MM-DD) */
export const addDaysISO = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
export const shiftMonth = (m: string, d: number) => {
  const x = new Date(`${m}-01T00:00:00Z`);
  x.setUTCMonth(x.getUTCMonth() + d);
  return x.toISOString().slice(0, 7);
};
export const monthLabel = (m: string) => `Tháng ${Number(m.slice(5))}/${m.slice(0, 4)}`;

export function cellStatus(c: TimesheetCell, date: string, today: string): AttendanceCellStatus {
  return attendanceCellStatus(c, date, today);
}

/** Ký hiệu ô: kiểu xem Số công (1 · 0,5 · V · ! · – · CN) hoặc Sáng | Chiều (S/C có công). */
export function cellText(c: TimesheetCell, date: string, today: string, view: 'work' | 'session'): string {
  const st = attendanceCellStatus(c, date, today);
  if (view === 'session' && c.sessions && (st === 'full' || st === 'half' || st === 'late')) {
    return `${c.sessions.am > 0 ? 'S' : '·'}${c.sessions.pm > 0 ? 'C' : '·'}`;
  }
  return attendanceCellSymbol(st, c.work, date, c.leave);
}

export const fmtWork = (v: number) => formatWork(Math.round(v * 100) / 100);

/** Link Google Maps cho tọa độ GPS lúc chấm. */
export const mapsUrl = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`;

/** Ô lịch "Bảng công của tôi" (anh Tiến 03/11/2026: hiện công NV giống quản trị: P · KL · LCN · V · Trễ). Ngày đi trễ / về sớm ghi "Trễ" thay số công. */
export function myCellText(c: TimesheetCell, date: string, today: string): string {
  return myAttendanceCellText(c, date, today);
}

export interface MyMonthCounts {
  /** Ngày nghỉ phép có lương (nửa ngày = 0,5) */
  p: number;
  /** Ngày nghỉ không lương (nửa ngày = 0,5) */
  kl: number;
  /** Số ngày làm Chủ nhật / ngày nghỉ tuần (có lệnh đã xác nhận hoặc có công) */
  lcn: number;
  /** Ngày vắng (ngày thường đã qua, không chấm, không đơn) */
  v: number;
}

/** Đếm P / KL / LCN / V của một tháng, cùng quy tắc ô bảng công quản trị (attendanceCellStatus). Trễ lấy totals.lateCount của API. */
export function myMonthCounts(cells: Record<string, TimesheetCell>, days: readonly string[], today: string): MyMonthCounts {
  const out: MyMonthCounts = { p: 0, kl: 0, lcn: 0, v: 0 };
  for (const d of days) {
    const c = cells[d];
    if (!c) continue;
    if (c.leave && !c.offDay) {
      if (c.leave.paid) out.p += c.leave.fraction;
      else out.kl += c.leave.fraction;
    }
    if (c.offDay && (c.sundayWork || c.work > 0)) out.lcn += 1;
    if (attendanceCellStatus(c, d, today) === 'absent') out.v += 1;
  }
  out.p = Math.round(out.p * 100) / 100;
  out.kl = Math.round(out.kl * 100) / 100;
  return out;
}
